import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import axios from 'axios';
import config from 'src/config';
import { useAuth } from 'src/util/authenticationProvider';
import { Lottery } from 'src/util/types';
import { Link as RouterLink } from 'react-router-dom';
import LotteryEdit from 'src/content/pages/management/LotteryEdit';
import { useTranslation } from 'react-i18next';

export default function ActiveLotteries() {
  const { tokens } = useAuth();
  const { t } = useTranslation();
  const [lotteries, setLotteries] = useState<Lottery[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editTarget, setEditTarget] = useState<Lottery | null>(null);

  const fetchActive = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${config.apiUrl}/api/lottery/current`, {
        headers: tokens?.accessToken ? { Authorization: `Bearer ${tokens.accessToken}` } : undefined
      });
      const payload = res?.data?.content ?? res?.data ?? [];
      const list = Array.isArray(payload) ? payload : payload ? [payload] : [];
      setLotteries(list);
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message || 'Failed to load active lotteries');
    } finally {
      setLoading(false);
    }
  }, [tokens?.accessToken]);

  useEffect(() => {
    fetchActive();
    const intervalId = window.setInterval(fetchActive, 60_000);
    return () => window.clearInterval(intervalId);
  }, [fetchActive]);

  const formatDate = (value: string) => {
    if (!value) return '';
    return new Date(value).toLocaleString();
  };

  const lotteryLink = (id: string) => `${window.location.origin}/lottery/${id}`;

  const handleShare = async (lottery: Lottery) => {
    const url = lotteryLink(lottery.id);
    try {
      if (navigator.share) {
        await navigator.share({ title: lottery.name ?? 'Lotterie', url });
        return;
      }
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
        return;
      }
      setError('Sharing is not supported in this browser');
    } catch (err: any) {
      setError(err?.message || 'Unable to share link');
    }
  };

  const handleCopy = async (lottery: Lottery) => {
    const url = lotteryLink(lottery.id);
    try {
      if (!navigator.clipboard?.writeText) {
        setError('Copy is not supported in this browser');
        return;
      }
      await navigator.clipboard.writeText(url);
    } catch (err: any) {
      setError(err?.message || 'Unable to copy link');
    }
  };

  const handleEdit = (lottery: Lottery) => {
    setEditTarget(lottery);
  };

  const handleCloseEdit = () => setEditTarget(null);

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        {t('lottery.activeLotteries', { defaultValue: 'Active Lotteries' })}
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : lotteries.length === 0 ? (
        <Typography>No active lotteries found.</Typography>
      ) : (
        <Table size="small">
          <TableHead>
              <TableRow>
              <TableCell>{t('lottery.name')}</TableCell>
              <TableCell>{t('lottery.startDate')}</TableCell>
              <TableCell>{t('lottery.endDate')}</TableCell>
              <TableCell>{t('lottery.active')}</TableCell>
              <TableCell align="right">{t('general.Actions', { defaultValue: 'Actions' })}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {lotteries.map((lottery) => (
              <TableRow key={lottery.id}>
                <TableCell>{lottery.name}</TableCell>
                <TableCell>{formatDate(lottery.startDate)}</TableCell>
                <TableCell>{formatDate(lottery.endDate)}</TableCell>
                <TableCell>{lottery.active ? 'Yes' : 'No'}</TableCell>
                <TableCell>{lottery.visibleToChildren ? 'Yes' : 'No'}</TableCell>
                <TableCell align="right">
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Button
                      component={RouterLink}
                      to={`/lottery/${lottery.id}`}
                      variant="outlined"
                      size="small"
                    >
                      {t('general.View', { defaultValue: 'View' })}
                    </Button>
                    <Button variant="outlined" size="small" onClick={() => handleShare(lottery)}>
                      {t('general.Share', { defaultValue: 'Share' })}
                    </Button>
                    <Button variant="outlined" size="small" onClick={() => handleCopy(lottery)}>
                      {t('general.CopyLink', { defaultValue: 'Copy link' })}
                    </Button>
                    <Button variant="contained" size="small" onClick={() => handleEdit(lottery)}>
                      {t('general.Edit', { defaultValue: 'Edit' })}
                    </Button>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Dialog open={Boolean(editTarget)} onClose={handleCloseEdit} fullWidth maxWidth="lg">
        <DialogTitle>{t('lottery.editTitle')}</DialogTitle>
        <DialogContent dividers>
          {editTarget && <LotteryEdit {...editTarget} />}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEdit}>{t('general.Close', { defaultValue: 'Close' })}</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
